import React from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import SPAButton from '../../shared/SPAButton';
import SPAPageHeader from '../../shared/SPAPageHeader';
import FailureDialog from './FailureDialog';
import ReasonList from './ReasonList';
import ReportFilters from './ReportFilters';
import SummaryStrip from './SummaryStrip';
import { reportParamsFrom, toQueryParams } from './reportParams';
import useAISafetyReport from './useAISafetyReport';
import type { ReportParams } from './types';

// AIDEV-NOTE: Reads /admin/ai_safety_reports.json and mounts both inside the SPA (/dev/ai-safety)
// and on the legacy admin page (components/admin/AISafetyReport), so it must not touch SPANavSlot.
// feature/from/to live in the URL under the controller's own param names.
const AISafetyReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = reportParamsFrom(searchParams);
  const { data, error } = useAISafetyReport(params);

  const applyParams = (next: ReportParams) => setSearchParams(toQueryParams(next));
  const failureId = searchParams.get('failure');
  const closeFailure = () => setSearchParams((previous) => {
    const next = new URLSearchParams(previous);
    next.delete('failure');
    return next;
  });
  const csvHref = Routes.admin_ai_safety_reports_path({ ...toQueryParams(params), format: 'csv' });
  const checkNames = data ? Object.keys(data.checks) : [];
  // Remounts the filter form when the URL changes, and again once a deep-linked district's name arrives.
  const filtersKey = [params.feature, params.from, params.to, params.subscriberId, data?.subscriber?.name].join('|');

  return (
    <div className="tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <SPAPageHeader
          actions={(
            <SPAButton asChild variant="secondary">
              <a href={csvHref}>Export CSV</a>
            </SPAButton>
          )}
          title="AI Safety"
        />
        <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-secondary">
          Post-generation screening of AI-written questions for hallucination and bias. This is the
          summary of findings reported for Exhibit G of the CA-NDPA.
        </p>

        <ReportFilters
          key={filtersKey}
          params={params}
          subscriber={data?.subscriber ?? null}
          onApply={applyParams}
        />

        {error ? (
          <p className="tw-m-0 tw-text-sm tw-text-spa-error">Failed to load the AI safety report.</p>
        ) : (
          <>
            <SummaryStrip from={params.from} report={data} to={params.to} />

            {data && checkNames.length === 0 && (
              <p className="tw-m-0 tw-text-sm tw-text-spa-tertiary">No screening results in this range.</p>
            )}
            {data && checkNames.length > 0 && (
              <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
                {checkNames.map(name => <ReasonList key={name} check={data.checks[name]} name={name} />)}
              </div>
            )}
            <FailureDialog failureId={failureId} onClose={closeFailure} />

            <p className="tw-mb-0 tw-mt-8 tw-text-xs tw-leading-relaxed tw-text-spa-tertiary">
              Reasons are written by the screening model and shown for failures only, up to the 100 most
              recent per check. The CSV export carries every screened row with subject, subscriber, model,
              and prompt version, up to 50,000 rows; narrow the range past that. Question text is never
              included.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AISafetyReport;
